<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AddPanel from '../AddPanel.vue';
import PlayerCardPreview from '../material/PlayerCardPreview.vue';
import VideoLinkButton from '../material/VideoLinkButton.vue';
import { useAuth } from '../../composables/useAuth.js';
import { useConfirm } from '../../composables/useConfirm.js';
import { useProfilePdf } from '../../composables/useProfilePdf.js';
import { useSnackbar } from '../../composables/useSnackbar.js';
import { cardGaps } from '../../domain/playerCard.js';

const props = defineProps({
  player: { type: Object, required: true },
});

const { can, role } = useAuth();
const { confirm } = useConfirm();
const { t } = useI18n();
const { downloadCard } = useProfilePdf();
const { show } = useSnackbar();
const canEdit = computed(() => can('media', 'write'));
/* Objekt aus der öffentlichen Ansicht (Gast). Seit F4 (supabase/gaeste-material.sql) liefert die Ansicht Highlight, Matches
   und Schlagwörter; fehlt die SQL noch oder gibt es keinen Wert, bliebe ein Abschnitt leer und würde "noch nichts"
   behaupten — deshalb erscheint bei Gästen ein Abschnitt nur mit Inhalt. */
const publicOnly = computed(() => props.player._publicOnly === true);

/* ---------- Player card: wird aus Player Info und Material erzeugt (D2) ----------
   Es gibt keine hochgeladene Spielerkarte mehr. Der Abschnitt gilt für jede Rolle, die den Reiter sieht — seit F4 auch
   für Gäste (die Karte entsteht im Browser aus denselben Feldern wie für alle, domain/profileSheet.js). Listen-Export
   und Markieren bleiben für Gäste gesperrt. Das alte Kartenfeld im Datensatz bleibt unangetastet. */
const showCard = computed(() => true);
const isGuest = computed(() => role?.value === 'guest');
/* Fehlende Schlagwörter nennt nur, wer sie in Player Info setzen darf (Zusatz dazu); Leser sehen die übrigen Lücken. */
const canSetKeywords = computed(() => can('players', 'write'));
/* Gäste können nichts ergänzen: kein Hinweis auf Lücken */
const gaps = computed(() => (isGuest.value ? [] : cardGaps(props.player).filter((k) => k !== 'traits' || canSetKeywords.value)));
const gapText = computed(() =>
  gaps.value.map((k) => t(k === 'traits' ? 'material.gap.traitsEdit' : `material.gap.${k}`)).join(', ')
);
const cardBusy = ref(false);

async function onDownloadCard() {
  if (cardBusy.value) return;
  cardBusy.value = true;
  try {
    await downloadCard(props.player);
  } catch (e) {
    console.error('[material] Player Card konnte nicht erzeugt werden:', e && e.message);
    show(t('material.cardFailed'), 'error');
  } finally {
    cardBusy.value = false;
  }
}

/* ---------- Highlight: genau ein Platz, wie die Spielerkarte ---------- */

const highlight = computed(() => props.player.highlightVideo || null);

const draftHighlightTitle = ref('');
const draftHighlightUrl = ref('');

function addHighlight({ close }) {
  const title = draftHighlightTitle.value.trim();
  const url = draftHighlightUrl.value.trim();
  if (!title || !url) return;
  props.player.highlightVideo = { title, url };
  draftHighlightTitle.value = '';
  draftHighlightUrl.value = '';
  close();
}

async function removeHighlight() {
  const h = highlight.value;
  const ok = await confirm({
    title: t('confirm.deleteVideoTitle'),
    message: t('confirm.deleteVideo', { name: h?.title || '—' }),
  });
  if (!ok) return;
  props.player.highlightVideo = null;
}

/* ---------- Matches: beliebig viele, mit Gegner-Angaben ---------- */

const matches = computed(() => props.player.videos || []);

const draftMatchTitle = ref('');
const draftMatchUrl = ref('');
const draftOpponent = ref('');
const draftShirtNumber = ref('');
const draftShirtColor = ref('');
const draftStats = ref('');

function resetMatchDraft() {
  draftMatchTitle.value = '';
  draftMatchUrl.value = '';
  draftOpponent.value = '';
  draftShirtNumber.value = '';
  draftShirtColor.value = '';
  draftStats.value = '';
}

/* Kurzangaben zum Gegner-Match als eine Zeile, nur die belegten Teile.
   Keine Vorschaubilder dafür — nur Text neben dem Link (siehe TODO
   „Match-Links im Reiter Material", 2026-09-23). */
function matchInfo(v) {
  const parts = [];
  if (v.opponent) parts.push(t('material.vsOpponent', { opponent: v.opponent }));
  if (v.shirtNumber) parts.push(`#${v.shirtNumber}`);
  if (v.shirtColor) parts.push(v.shirtColor);
  if (v.stats) parts.push(v.stats);
  return parts.join(' · ');
}

/* Die Gegner-Angaben stehen gleich in diesem Formular, nicht erst in einem
   nachträglichen Bearbeiten-Schritt (Entscheidung Bastian, siehe TODO
   „Material-Reiter: Highlights/Matches nachbessern"). Bearbeiten gibt es für
   Matches nicht mehr — ein falscher Eintrag wird gelöscht und neu angelegt. */
function addMatch({ close }) {
  const title = draftMatchTitle.value.trim();
  const url = draftMatchUrl.value.trim();
  if (!title || !url) return;
  if (!props.player.videos) props.player.videos = [];
  props.player.videos.push({
    title,
    url,
    opponent: draftOpponent.value.trim(),
    shirtNumber: draftShirtNumber.value.trim(),
    shirtColor: draftShirtColor.value.trim(),
    stats: draftStats.value.trim(),
  });
  resetMatchDraft();
  close();
}

async function removeMatch(i) {
  const v = props.player.videos[i];
  const ok = await confirm({
    title: t('confirm.deleteVideoTitle'),
    message: t('confirm.deleteVideo', { name: v?.title || '—' }),
  });
  if (!ok) return;
  props.player.videos.splice(i, 1);
}
</script>

<template>
  <div>
    <!-- ---------- Player card: automatisch erzeugt ---------- -->
    <div v-if="showCard" class="doc-group" data-testid="card-section">
      <h3>{{ $t('material.playerCard') }}</h3>
      <!-- Vorschau: oberes Drittel der echten Karte, nur Anzeige; erzeugt erst, wenn der Abschnitt sichtbar ist -->
      <PlayerCardPreview :player="player" />
      <p class="card-note">{{ $t('material.cardHint') }}</p>
      <div class="card-actions">
        <button class="btn mat-btn mat-primary" data-testid="card-download" :disabled="cardBusy" @click="onDownloadCard">
          {{ cardBusy ? $t('common.loading') : $t('material.downloadCard') }}
        </button>
      </div>
      <p v-if="gaps.length" class="card-gaps" data-testid="card-gaps">{{ $t('material.cardMissing', { items: gapText }) }}</p>
    </div>

    <!-- ---------- Highlight: genau ein Video-Slot ---------- -->
    <div v-if="!publicOnly || highlight" class="doc-group spaced-top" data-testid="highlight-section">
      <h3>{{ $t('material.highlight') }}</h3>

      <template v-if="highlight">
        <div class="video-row">
          <div class="v-top">
            <div class="v-meta">
              <div class="v-title">{{ highlight.title }}</div>
            </div>
            <VideoLinkButton :url="highlight.url" />
            <button v-if="canEdit" class="icon-btn mat-quiet mat-danger" @click="removeHighlight">
              {{ $t('common.delete') }}
            </button>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="feed-empty">{{ $t('material.noHighlight') }}</div>
      </template>

      <AddPanel
        v-if="canEdit && !highlight"
        :label="$t('material.addHighlight')"
        :invalid="!draftHighlightTitle.trim() || !draftHighlightUrl.trim()"
        @submit="addHighlight"
        @cancel="draftHighlightTitle = ''; draftHighlightUrl = ''"
      >
        <input v-model="draftHighlightTitle" type="text" :placeholder="$t('material.videoTitle')" />
        <input v-model="draftHighlightUrl" type="text" placeholder="https://youtube.com/watch?v=…" />
      </AddPanel>
    </div>

    <!-- ---------- Matches: beliebig viele, mit Gegner-Angaben ---------- -->
    <div v-if="!publicOnly || matches.length" class="doc-group spaced-top" data-testid="matches-section">
      <h3>{{ $t('material.matches') }}</h3>

      <div v-if="!matches.length" class="feed-empty">{{ $t('material.noMatches') }}</div>

      <div v-for="(v, i) in matches" :key="i" class="video-row">
        <div class="v-top">
          <div class="v-meta">
            <div class="v-title">{{ v.title }}</div>
            <div v-if="matchInfo(v)" class="ud-sub">{{ matchInfo(v) }}</div>
          </div>
          <VideoLinkButton :url="v.url" />
          <button v-if="canEdit" class="icon-btn mat-quiet mat-danger" @click="removeMatch(i)">
            {{ $t('common.delete') }}
          </button>
        </div>
      </div>

      <AddPanel
        v-if="canEdit"
        :label="$t('material.addMatch')"
        :invalid="!draftMatchTitle.trim() || !draftMatchUrl.trim()"
        @submit="addMatch"
        @cancel="resetMatchDraft"
      >
        <input v-model="draftMatchTitle" type="text" :placeholder="$t('material.matchTitlePlaceholder')" />
        <input v-model="draftMatchUrl" type="text" placeholder="https://youtube.com/watch?v=…" />
        <div class="match-field-row">
          <input
            v-model="draftOpponent"
            type="text"
            :placeholder="$t('material.opponentPlaceholder')"
            class="grow"
          />
          <input
            v-model="draftShirtNumber"
            type="text"
            :placeholder="$t('material.shirtNumberPlaceholder')"
            class="narrow"
          />
        </div>
        <div class="match-field-row">
          <input
            v-model="draftShirtColor"
            type="text"
            :placeholder="$t('material.shirtColorPlaceholder')"
          />
          <input
            v-model="draftStats"
            type="text"
            :placeholder="$t('material.statsPlaceholder')"
            class="grow"
          />
        </div>
      </AddPanel>
    </div>

  </div>
</template>

<style scoped>
.card-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
}
.card-note {
  margin: 0;
  font-size: 0.85rem;
  color: var(--muted);
}
.card-gaps {
  margin: 10px 0 0;
  font-size: 0.8rem;
  color: var(--dim);
}
.spaced-top {
  margin-top: 18px;
}
.v-meta {
  flex: 1;
  min-width: 0;
}
.match-field-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}
.match-field-row .grow {
  flex: 1;
  min-width: 160px;
}
.match-field-row .narrow {
  width: 90px;
}
.btn:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
